import { Theme as AugmentedTheme, useTheme } from '@mui/material/styles';


import { makeStyles } from 'tss-react/mui';
import React, { useState } from 'react';
import {
  Button,
  ContentTemplate,
  Divider,
  Grid,
  Icon,
  KeyValue,
  Link,
  Pill,
  Progress,
  SectionHeader,
  TitleHeader,
} from 'web-library';
import { IconName } from 'web-library/stories/atom/Icon/Icon';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import RouteKey from '../../routes/key';
import { candidateService } from '../../api';
import PersonalDetails from '../../modules/appeal/components/summary/PersonalDetails';
import SelectedSubjectsReview from '../../modules/appeal/components/summary/SelectedSubjectsReview';
import TransactionDetails from '../../modules/appeal/components/summary/TransactionDetails';
import AppealFees from '../../modules/appeal/components/@candidate/AppealFees';
import {
  CoordinatorAppealSummaryResponse,
  useGetCoordinatorAppealSummary,
} from '../../api-refactored/appeal/useGetCoordinatorAppealSummary';
import { stringToLongDateAndShortTime } from '../../utils/DateUtils';
import AppealOutcome from '../../modules/appeal/components/summary/AppealOutcome';
import { FileUploadState } from '../../models/File';
import DownloadProgressModal from '../../components/modals/DownloadProgressModal';

const { appeal } = RouteKey.child;
const { applicationsCoordinator } = RouteKey.appeal.child;

const useStyles = makeStyles()((theme) =>
({
  title: {
    marginBottom: theme.spacing(2),
  },
  button: {
    height: 'fit-content',
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(7),
    marginRight: theme.spacing(2),
  },
  downloadPdf: {
    display: 'flex',
    paddingLeft: theme.spacing(3),
  },
  downloadPdfText: {
    fontWeight: 800,
    paddingLeft: theme.spacing(1),
  },
  container: {
    maxWidth: 960,
    marginBottom: theme.spacing(2),
  },
  [theme.breakpoints.down('md')]: {
    statusPill: {
      marginTop: theme.spacing(1),
    },
  },
}));

const SectionHeaderAdditionalComponent = (
  downloadAppealSummary: () => void,
  isFetchingDownloadAppealSummary: boolean,
  theme: AugmentedTheme,
  classes: Record<string,string>
) =>
  isFetchingDownloadAppealSummary ? (
    <Icon name={IconName.loadingIndicator} size="L" color={theme.color.link.linkActive02} />
  ) : (
    <Link
      className={classes.downloadPdf}
      id="link-download"
      onClick={() => downloadAppealSummary()}
    >
      <Icon name={IconName.download} size="M" color={theme.color.link.linkDefault01} />
      <span className={classes.downloadPdfText}>Download Summary</span>
    </Link>
  );

const AppealSummaryPillLabel = (getAppealSummary: CoordinatorAppealSummaryResponse) =>
  getAppealSummary.applicationStatus.status === 'PENDING_PAYMENT'
    ? 'Pending payment'
    : getAppealSummary.applicationStatus.status;

const AppealSummaryApplicationDateTimeValue = (
  getAppealSummary: CoordinatorAppealSummaryResponse
) =>
  getAppealSummary.applicationStatus.applicationDateTime
    ? stringToLongDateAndShortTime(getAppealSummary.applicationStatus.applicationDateTime)
    : '-';

interface CoordinatorAppealSummaryProps {
  isPaymentLink?: boolean;
}

const AppealSummaryPillVariant = (getAppealSummary: CoordinatorAppealSummaryResponse) =>
  getAppealSummary.applicationStatus.status === 'PENDING_PAYMENT' ? 'info' : 'positive';

const CoordinatorAppealSummary = ({ isPaymentLink }: CoordinatorAppealSummaryProps) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const theme = useTheme();
  const history = useHistory<{ page: number; pageSize: number }>();
  const location = useLocation<{ page: number; pageSize: number }>();
  const { uuid } = useParams<{ uuid: string }>();
  const [fileStatus, setFileStatus] = useState(FileUploadState.IDLE);

  const {
    data: getAppealSummary,
    isLoading: isLoadingAppealSummary,
  } = useGetCoordinatorAppealSummary({
    uuid,
  });

  const {
    refetch: downloadAppealSummary,
    isFetching: isFetchingDownloadAppealSummary,
  } = candidateService.appeal.candidate.useCandidateDownloadSummary(
    (download) => {
      const newWindow = window.open(download?.url, '_blank', 'noopener,noreferrer');
      if (newWindow) {
        newWindow.opener = null;
      }
    },
    (_) => {
      setFileStatus(FileUploadState.FAILED);
    },
    uuid
  );

  const handleCloseDownloadErrorPrompt = () => {
    setFileStatus(FileUploadState.IDLE);
  };

  return (
    <>
      {!isPaymentLink && (
        <TitleHeader
          title={t('appeal->>AppealCoordinatorSummary.AppealForReviewOfResultsDetails')}
        />
      )}
      {isLoadingAppealSummary ? (
        <ContentTemplate>
          <Progress color="secondary" />
        </ContentTemplate>
      ) : (
        <>
          <ContentTemplate type="full">
            {getAppealSummary && (
              <>
                <Grid container>
                  <Grid xs={12}>
                    <SectionHeader
                      title={t('appeal->>AppealSummary.ApplicationDetails')}
                    >
                      {SectionHeaderAdditionalComponent(
                          downloadAppealSummary,
                          isFetchingDownloadAppealSummary,
                          theme,
                          classes
                      )}
                    </SectionHeader>
                  </Grid>
                  <Grid className={classes.container} xs={12}>
                    <Grid container direction="row">
                      <Grid md={6} sm={12}>
                        <KeyValue
                          id="applicationId"
                          keyText={t('appeal->>AppealSummary.ApplicationStatus.ID')}
                          valueText={getAppealSummary.applicationStatus.applicationId}
                        />
                      </Grid>
                      <Grid md={6} sm={12} xs={12}>
                        <KeyValue
                          id="applicationStatus"
                          keyText={t('appeal->>AppealSummary.ApplicationStatus.Status')}
                          valueText={
                            <Grid className={classes.statusPill}>
                              <Pill
                                label={AppealSummaryPillLabel(getAppealSummary)}
                                variant={AppealSummaryPillVariant(getAppealSummary)}
                              />
                            </Grid>
                          }
                        />
                      </Grid>
                    </Grid>
                    {getAppealSummary.applicationStatus.coordinatorName && (
                      <>
                        <Grid container>
                          <Grid xs={12} sm={6}>
                            <KeyValue
                              keyText={t('appeal->>AppealCoordinatorReceipt.ApplicationDateTime')}
                              valueText={AppealSummaryApplicationDateTimeValue(getAppealSummary)}
                            />
                          </Grid>
                        </Grid>
                        <Grid container>
                          <Grid xs={12} sm={6}>
                            <KeyValue
                              id="applicationId"
                              keyText={t('appeal->>AppealCoordinatorReceipt.Coordinator')}
                              valueText={getAppealSummary.applicationStatus.coordinatorName ?? '-'}
                            />
                          </Grid>
                        </Grid>
                      </>
                    )}
                  </Grid>
                </Grid>

                <Divider />
                <AppealOutcome appealOutcome={getAppealSummary.applicationOutcome} />
                <Divider />

                <Grid container>
                  <Grid xs={12}>
                    <PersonalDetails {...getAppealSummary.candidateDetails} />
                  </Grid>
                </Grid>

                <Divider />

                <Grid container>
                  <Grid xs={12}>
                    <SelectedSubjectsReview {...getAppealSummary.selectedSubjects} />
                  </Grid>
                </Grid>

                <Divider />

                <Grid container>
                  <Grid xs={12}>
                    <AppealFees {...getAppealSummary.appealFees} />
                  </Grid>
                </Grid>

                <Divider />

                <Grid container>
                  <Grid xs={12}>
                    <TransactionDetails {...getAppealSummary.transactionDetails} isGroup />
                  </Grid>
                </Grid>

                {!isPaymentLink && (
                  <Grid container>
                    <Grid xs={12} lg={5}>
                      <Button
                        id="button-appealSummary-goToAppealApplications"
                        className={classes.button}
                        onClick={() =>
                          history.push(`/${appeal}/${applicationsCoordinator.route}`, {
                            pageSize: location?.state?.pageSize,
                            page: location?.state?.page,
                          })
                        }
                        variant="outlined"
                      >
                        {t('appeal->>AppealReceipt.BackToAppealApplications')}
                      </Button>
                    </Grid>
                  </Grid>
                )}
              </>
            )}
          </ContentTemplate>
          {fileStatus === FileUploadState.FAILED && (
            <DownloadProgressModal
              modalState
              fileDownloadStatus={FileUploadState.FAILED}
              onDialogClose={handleCloseDownloadErrorPrompt}
            />
          )}
        </>
      )}
    </>
  );
};

export default CoordinatorAppealSummary;
